import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import clsx from 'clsx';
import { RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from 'components/ui/Button';
import { useTable } from '../TableContext';
import { useTableFiltering } from '../hooks/useTableFiltering';
import {
  BooleanFacet,
  DEFAULT_FACET_LABELS,
  Facet,
  FacetLabelCache,
  FacetPeriod,
  FacetQueryCommand,
  FacetValue,
  createFacetLabelCache,
  mapFacetFiltersToFiltersDto
} from '../facetFilters';

/**
 * Au-delà de ce nombre d'options, une facette multiselect affiche un champ de
 * recherche et plafonne la liste (« + N autres — affinez »). Le back envoie déjà
 * TOUTES les options d'une facette (même 500 courtiers) : la recherche filtre
 * côté front dans cette liste, aucun appel réseau.
 * Cible : 10 — abaissé à 3 le temps des tests en local.
 */
const MAX_FACET_OPTIONS = 5;

/** Comparaison de libellés insensible à la casse et aux accents. */
const normalizeLabel = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

interface FacetFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Charge les facettes avec le body courant (searchString + filtres mappés).
   * Typiquement : (cmd) => xxxAPI.getXxxListFacets({ getGenericListCommandDto: cmd }).
   */
  fetchFacets: (command: FacetQueryCommand) => Promise<{ facets: Facet[] }>;
  /** Cases à cocher booléennes ajoutées au-dessus des facettes back (ex. isTest). */
  booleanFacets?: BooleanFacet[];
  /**
   * Mémoire partagée des libellés (options + dimensions), pour que les chips des
   * filtres actifs (FacetActiveFilters) restent lisibles même drawer fermé.
   */
  labelCache?: FacetLabelCache;
}

/**
 * Panneau latéral de filtres façon e-commerce, piloté par la réponse
 * {domaine}/liste/filtres : on boucle sur les facettes et on rend selon leur type —
 * aucune dimension codée en dur. Ouvert par le bouton « Filtrer » natif du tableau.
 *
 * Les sélections vivent dans le state de filtres du TableContext, indexées par
 * facet.key (refetch de la liste + reset pagination gérés par le contexte). Les
 * facettes sont rechargées à l'ouverture et à chaque changement de filtre ou de
 * recherche (debounce 300 ms) — pas au changement de page. Une valeur cochée qui
 * disparaît des options reste affichée cochée et grisée : jamais décochée en douce.
 */
export function FacetFilterDrawer({
  isOpen,
  onClose,
  fetchFacets,
  booleanFacets,
  labelCache
}: FacetFilterDrawerProps) {
  const { state } = useTable();
  const { filters, setFilter, removeFilter, clearFilters, hasActiveFilters } =
    useTableFiltering();

  const [facets, setFacets] = useState<Facet[] | null>(null);
  const [isLoadingFacets, setIsLoadingFacets] = useState(false);
  const [facetsError, setFacetsError] = useState<string | null>(null);
  // Recherche locale par facette (facet.key -> saisie) : filtre la liste déjà
  // chargée, aucun appel réseau. Réinitialisée à chaque ouverture du drawer.
  const [facetSearches, setFacetSearches] = useState<Record<string, string>>({});
  // Facettes dépliées (« + N autres » cliqué) : plafond levé, la recherche reste active.
  const [expandedFacets, setExpandedFacets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      setFacetSearches({});
      setExpandedFacets({});
    }
  }, [isOpen]);
  // Mémoire des libellés déjà vus (chips des valeurs disparues + chips actifs)
  const internalCacheRef = useRef<FacetLabelCache>(createFacetLabelCache());
  const cache = labelCache ?? internalCacheRef.current;

  const filtersSignature = JSON.stringify(filters);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setIsLoadingFacets(true);
      setFacetsError(null);
      try {
        const res = await fetchFacets({
          pageNumber: 1,
          pageSize: state.pageSize,
          searchString: state.searchString || undefined,
          filters: mapFacetFiltersToFiltersDto(filters)
        });
        if (cancelled) return;
        res.facets.forEach((facet) => {
          cache.facets.set(facet.key, facet.label);
          facet.values.forEach((v) => cache.values.set(v.id, v.label));
        });
        setFacets(res.facets);
      } catch {
        if (!cancelled) setFacetsError('Impossible de charger les filtres.');
      } finally {
        if (!cancelled) setIsLoadingFacets(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // filtersSignature couvre filters ; pas de refetch au changement de page (voulu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, state.searchString, filtersSignature, fetchFacets]);

  const toggleValue = useCallback(
    (facetKey: string, valueId: string) => {
      const current: string[] = filters[facetKey] ?? [];
      const next = current.includes(valueId)
        ? current.filter((id) => id !== valueId)
        : [...current, valueId];
      if (next.length > 0) {
        setFilter(facetKey, next);
      } else {
        removeFilter(facetKey);
      }
    },
    [filters, setFilter, removeFilter]
  );

  const setPeriodPart = useCallback(
    (part: keyof FacetPeriod, value: string) => {
      const next: FacetPeriod = { ...(filters.period ?? {}), [part]: value || undefined };
      if (next.startDate || next.endDate) {
        setFilter('period', next);
      } else {
        removeFilter('period');
      }
    },
    [filters, setFilter, removeFilter]
  );

  const setBoolean = useCallback(
    (key: string, checked: boolean) => {
      // Tri-état géré en 1 case : on écrit toujours la valeur (true = uniquement,
      // false = exclure) — jamais removeFilter, pour rester au défaut « exclure ».
      setFilter(key, checked);
    },
    [setFilter]
  );

  const renderBooleanFacets = () => {
    if (!booleanFacets?.length) return null;
    return (
      <section>
        <h4 className="mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
          <span className="h-3.5 w-1 rounded-full bg-secondary" aria-hidden />
          Affichage
        </h4>
        <div className="space-y-0.5">
          {booleanFacets.map((bf) => {
            const isChecked = filters[bf.key] === true;
            return (
              <label
                key={bf.key}
                className={clsx(
                  'flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-primary/5',
                  isChecked && 'bg-primary/5'
                )}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => setBoolean(bf.key, e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="flex-1">
                  <span
                    className={clsx(
                      'block',
                      isChecked ? 'font-medium text-gray-900' : 'text-gray-600'
                    )}
                  >
                    {bf.label}
                  </span>
                  {bf.hint && (
                    <span className="mt-0.5 block text-xs text-gray-400">{bf.hint}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </section>
    );
  };

  const renderMultiselect = (facet: Facet) => {
    const selected: string[] = filters[facet.key] ?? [];
    // Valeurs cochées éliminées par les autres filtres : conservées, grisées (count 0)
    const missingSelected: FacetValue[] = selected
      .filter((id) => !facet.values.some((v) => v.id === id))
      .map((id) => ({ id, label: cache.values.get(id) ?? id, count: 0 }));
    const options = [...facet.values, ...missingSelected];

    // Facette volumineuse : champ de recherche + liste plafonnée. Les valeurs cochées
    // restent toujours visibles (épinglées en tête), hors plafond et hors recherche.
    // « + N autres » déplie tout (le plafond saute, la recherche reste active).
    const isLarge = options.length > MAX_FACET_OPTIONS;
    const isExpanded = expandedFacets[facet.key] === true;
    const query = normalizeLabel((facetSearches[facet.key] ?? '').trim());
    let visibleOptions = options;
    let hiddenCount = 0;
    if (isLarge) {
      const checked = options.filter((v) => selected.includes(v.id));
      const unchecked = options.filter(
        (v) =>
          !selected.includes(v.id) &&
          (!query || normalizeLabel(v.label).includes(query))
      );
      visibleOptions = [
        ...checked,
        ...(isExpanded ? unchecked : unchecked.slice(0, MAX_FACET_OPTIONS))
      ];
      hiddenCount = isExpanded
        ? 0
        : unchecked.length - Math.min(unchecked.length, MAX_FACET_OPTIONS);
    }

    return (
      <div>
        {isLarge && (
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={facetSearches[facet.key] ?? ''}
              onChange={(e) =>
                setFacetSearches((prev) => ({ ...prev, [facet.key]: e.target.value }))
              }
              placeholder="Rechercher…"
              className="h-9 w-full rounded-lg border border-gray-200 pl-8 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label={`Rechercher dans ${facet.label}`}
            />
          </div>
        )}
        <div className="max-h-64 space-y-0.5 overflow-y-auto pr-1">
        {visibleOptions.map((value) => {
          const isChecked = selected.includes(value.id);
          const isGhost = value.count === 0 && isChecked;
          return (
            <label
              key={value.id}
              className={clsx(
                'group flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-primary/5',
                isChecked && 'bg-primary/5',
                isGhost && 'opacity-50'
              )}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleValue(facet.key, value.id)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span
                className={clsx(
                  'flex-1 truncate',
                  isChecked ? 'font-medium text-gray-900' : 'text-gray-600'
                )}
              >
                {value.label}
              </span>
              <span
                className={clsx(
                  'rounded-full px-2 py-0.5 text-xs tabular-nums',
                  isChecked
                    ? 'bg-primary/10 font-semibold text-primary'
                    : 'bg-gray-100 text-gray-500'
                )}
              >
                {value.count}
              </span>
            </label>
          );
        })}
        {options.length === 0 && (
          <p className="px-2 py-1.5 text-xs italic text-gray-400">
            Aucune option disponible
          </p>
        )}
        {isLarge && visibleOptions.length === 0 && (
          <p className="px-2 py-1.5 text-xs italic text-gray-400">
            Aucun résultat pour cette recherche
          </p>
        )}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() =>
              setExpandedFacets((prev) => ({ ...prev, [facet.key]: true }))
            }
            className="w-full rounded-lg px-2 py-1.5 text-left text-xs font-medium text-primary transition-colors hover:bg-primary/5"
          >
            + {hiddenCount} autre{hiddenCount > 1 ? 's' : ''} — tout afficher
          </button>
        )}
        {isLarge && isExpanded && (
          <button
            type="button"
            onClick={() =>
              setExpandedFacets((prev) => ({ ...prev, [facet.key]: false }))
            }
            className="w-full rounded-lg px-2 py-1.5 text-left text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            Réduire la liste
          </button>
        )}
        </div>
      </div>
    );
  };

  const renderDaterange = () => {
    const period: FacetPeriod = filters.period ?? {};
    const inputClass =
      'mt-1 h-10 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
    return (
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs font-medium text-gray-500">
          Du
          <input
            type="date"
            value={period.startDate ?? ''}
            max={period.endDate}
            onChange={(e) => setPeriodPart('startDate', e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block text-xs font-medium text-gray-500">
          Au
          <input
            type="date"
            value={period.endDate ?? ''}
            min={period.startDate}
            onChange={(e) => setPeriodPart('endDate', e.target.value)}
            className={inputClass}
          />
        </label>
      </div>
    );
  };

  const skeleton = (
    <div className="animate-pulse space-y-6">
      {[0, 1, 2].map((group) => (
        <div key={group}>
          <div className="mb-3 h-3 w-24 rounded bg-gray-200" />
          <div className="space-y-2">
            {[0, 1, 2, 3].map((row) => (
              <div key={row} className="h-7 rounded-lg bg-gray-100" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition duration-200 data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel
          transition
          className={clsx(
            'flex h-full w-full max-w-md flex-col bg-white shadow-2xl',
            'transition duration-300 ease-out data-[closed]:translate-x-full'
          )}
        >
          {/* En-tête */}
          <div className="flex items-center justify-between bg-primary px-5 py-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
              <SlidersHorizontal className="h-5 w-5" />
              Filtres
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fermer les filtres"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Contenu scrollable */}
          <div
            className={clsx(
              'flex-1 space-y-6 overflow-y-auto px-5 py-5',
              isLoadingFacets && facets && 'opacity-60'
            )}
          >
            {renderBooleanFacets()}
            {facetsError ? (
              <p className="text-sm text-red-600">{facetsError}</p>
            ) : !facets ? (
              skeleton
            ) : facets.length === 0 ? (
              !booleanFacets?.length && (
                <p className="text-sm text-gray-400">Aucun filtre disponible.</p>
              )
            ) : (
              facets.map((facet) => (
                <section key={facet.key}>
                  <h4 className="mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
                    <span className="h-3.5 w-1 rounded-full bg-secondary" aria-hidden />
                    {facet.label}
                  </h4>
                  {facet.type === 'daterange'
                    ? renderDaterange()
                    : renderMultiselect(facet)}
                </section>
              ))
            )}
          </div>

          {/* Pied fixe */}
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
            <Button outline onClick={clearFilters} disabled={!hasActiveFilters}>
              <RotateCcw data-slot="icon" />
              Réinitialiser
            </Button>
            <Button color="primary" onClick={onClose}>
              Voir les résultats
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

interface FacetActiveFiltersProps {
  /** Même cache que celui passé au drawer : libellés des options et des dimensions. */
  labelCache: FacetLabelCache;
  /** Facettes booléennes : une puce est affichée quand la valeur vaut true. */
  booleanFacets?: BooleanFacet[];
}

/** dd/mm/yyyy à partir d'un yyyy-mm-dd. */
const toFr = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

/**
 * Chips des filtres actifs, affichés entre l'en-tête et le tableau, avec un bouton
 * « Retirer les filtres ». Chaque chip affiche sa dimension (« Statut : En cours »)
 * et est retirable individuellement (×).
 */
export function FacetActiveFilters({ labelCache, booleanFacets }: FacetActiveFiltersProps) {
  const { filters, setFilter, removeFilter, clearFilters } = useTableFiltering();

  // Puces à afficher pour les booléens actifs (valeur true uniquement).
  const activeBooleanFacets = (booleanFacets ?? []).filter((bf) => filters[bf.key] === true);

  // hasActiveFilters compte aussi isTest=false (présent par défaut) ; on n'affiche le
  // bandeau que s'il y a réellement une puce à montrer (tableau/période ou booléen true).
  const hasArrayOrPeriod = Object.entries(filters).some(
    ([key, value]) => key === 'period' || (Array.isArray(value) && value.length > 0)
  );
  if (!hasArrayOrPeriod && activeBooleanFacets.length === 0) return null;

  const facetLabel = (key: string) =>
    labelCache.facets.get(key) ?? DEFAULT_FACET_LABELS[key] ?? key;

  const removeValue = (facetKey: string, valueId: string) => {
    const next = ((filters[facetKey] ?? []) as string[]).filter((id) => id !== valueId);
    if (next.length > 0) {
      setFilter(facetKey, next);
    } else {
      removeFilter(facetKey);
    }
  };

  const period: FacetPeriod | undefined = filters.period;
  const periodLabel = period
    ? period.startDate && period.endDate
      ? `du ${toFr(period.startDate)} au ${toFr(period.endDate)}`
      : period.startDate
        ? `depuis le ${toFr(period.startDate)}`
        : `jusqu'au ${toFr(period.endDate!)}`
    : undefined;

  const chipClass =
    'inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 py-1 pl-3 pr-1.5 text-xs text-primary';

  const chip = (key: string, valueLabel: string, onRemove: () => void, chipKey: string) => (
    <span key={chipKey} className={chipClass}>
      <span className="font-normal opacity-70">{facetLabel(key)}&nbsp;:</span>
      <span className="font-semibold">{valueLabel}</span>
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-0.5 transition-colors hover:bg-primary/20"
        aria-label={`Retirer le filtre ${facetLabel(key)}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-primary/10 bg-primary/[0.03] px-4 py-2.5">
      {Object.entries(filters).map(([key, value]) => {
        if (key === 'period' || !Array.isArray(value)) return null;
        return (value as string[]).map((id) =>
          chip(
            key,
            labelCache.values.get(id) ?? id,
            () => removeValue(key, id),
            `${key}:${id}`
          )
        );
      })}
      {periodLabel &&
        chip('period', periodLabel, () => removeFilter('period'), 'period')}
      {activeBooleanFacets.map((bf) =>
        // Retirer la puce ne supprime pas la clé : on revient au défaut (false = exclure).
        chip(bf.key, 'Oui', () => setFilter(bf.key, false), `bool:${bf.key}`)
      )}
      <button
        type="button"
        onClick={clearFilters}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800"
      >
        <RotateCcw className="h-3 w-3" />
        Retirer les filtres
      </button>
    </div>
  );
}

export default FacetFilterDrawer;
